import React, { useState, useEffect, useRef } from 'react';
import './live-dashboard.css';
import { SelectInputWithoutForm } from '../../components/form/select-input';
import { dashboard } from '../../services/InspectionOneServices';
import { getTodayUtcRange, getCurrentShiftId } from '../../utils/common';
import Authentication from '../../auth/Authentication';

interface Coordinate {
  x: number;
  y: number;
}

interface AnnotatedDetail {
  uniqueId: string;
  labelUniqueId?: string;
  labelName: string;
  coordinates: Coordinate[];
  stroke: string;
  strokeWidth: number;
  fill: string;
  isDeleted?: number;
}


interface PlateData {
  total: number;
  defect: number;
}

interface SideData {
  plates: PlateData;
  bunches: PlateData;
}

interface LineEntry {
  operator: SideData;
  nonOperator: SideData;
}

interface LiveDashboardMappingSide {
  plates: { id: string; total: string; defect: string };
  bunches: { id: string; total: string; defect: string };
}

interface LiveDashboardMapping {
  operator: LiveDashboardMappingSide;
  nonOperator: LiveDashboardMappingSide;
}

interface LiveDisplayImage {
  displayResult: any;
  panelName: string;
  timestamp: string;
  createdAt: string;
  annotationColors: string[];
}

const ZERO_LINE_ENTRY: LineEntry = {
  operator:    { plates: { total: 0, defect: 0 }, bunches: { total: 0, defect: 0 } },
  nonOperator: { plates: { total: 0, defect: 0 }, bunches: { total: 0, defect: 0 } },
};

const LIVE_DASHBOARD_POLL_MS = 15000;
const WEB_SPLIT_POLL_MS = 120000;
const EMPTY_LIVE_DASHBOARD_MAPPING: LiveDashboardMapping = {
  operator: {
    plates: { id: '', total: '', defect: '' },
    bunches: { id: '', total: '', defect: '' },
  },
  nonOperator: {
    plates: { id: '', total: '', defect: '' },
    bunches: { id: '', total: '', defect: '' },
  },
};

function resolveLineData(subProcessCardsData: any[], mapping: LiveDashboardMapping): LineEntry {
  const getVal = (id: string, statusKey: string): number => {
    const proc = subProcessCardsData.find((s: any) => s.subProcessUniqueId === id);
    return proc?.statuses.find((s: any) => s.uniqueId === statusKey)?.value ?? 0;
  };
  const resolve = (cfg: { id: string; total: string; defect: string }): PlateData => ({
    total: getVal(cfg.id, cfg.total),
    defect: getVal(cfg.id, cfg.defect),
  });
  return {
    operator: {
      plates: resolve(mapping.operator.plates),
      bunches: resolve(mapping.operator.bunches),
    },
    nonOperator: {
      plates: resolve(mapping.nonOperator.plates),
      bunches: resolve(mapping.nonOperator.bunches),
    },
  };
}




function sideStats(side: SideData) {
  const total = side.plates.total + side.bunches.total;
  const defect = side.plates.defect + side.bunches.defect;
  return {
    total,
    defect,
    defectRate: total > 0 ? (defect / total) * 100 : 0,
  };
}

function defectRateStr(defect: number, total: number): string {
  if (total === 0) return '—';
  return `${((defect / total) * 100).toFixed(1)}%`;
}

function defectBadgeClass(defect: number): string {
  return defect === 0 ? 'rate-ok' : 'rate-critical';
}

function parseLiveResultDate(createdAt: string): Date {
  return new Date(createdAt + 'Z');
}

function formatLiveResultTime(createdAt: string): string {
  const local = parseLiveResultDate(createdAt);
  const h = local.getHours();
  const m = String(local.getMinutes()).padStart(2, '0');
  const s = String(local.getSeconds()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = (h % 12) || 12;
  return `${h12}:${m}:${s} ${ampm}`;
}

function formatRelativeAge(createdAt: string, nowMs: number): string {
  const diffMs = Math.max(0, nowMs - parseLiveResultDate(createdAt).getTime());
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
}

function getRelativeAgeClass(createdAt: string, nowMs: number): string {
  const diffMs = Math.max(0, nowMs - parseLiveResultDate(createdAt).getTime());
  const minutes = Math.floor(diffMs / 60000);

  if (minutes < 5) return 'age-critical';
  if (minutes < 30) return 'age-warning';
  if (minutes < 120) return 'age-stale';
  return 'age-ok';
}

function getLiveResultSignature(liveResults: any[]): string {
  return liveResults
    .map((liveResult: any) => [
      liveResult.uniqueId ?? liveResult.id ?? '',
      liveResult.subProcessLogicUniqueId ?? '',
      liveResult.createdAt ?? '',
      liveResult.displayResult ?? '',
    ].join('|'))
    .join('::');
}

function parseIgnoreDefaultLabels(interactionConfigurations: any): string[] {
  if (!interactionConfigurations) return [];

  const configs =
    typeof interactionConfigurations === 'string'
      ? (() => {
        try {
          return JSON.parse(interactionConfigurations);
        } catch {
          return null;
        }
      })()
      : interactionConfigurations;

  if (!configs?.ignoreDefaultLabels) return [];

  return String(configs.ignoreDefaultLabels)
    .split(',')
    .map((labelId) => labelId.trim())
    .filter(Boolean);
}

function getSubProcessLogicList(processLogic: any): any[] {
  return (
    processLogic?.subProcessLogic ??
    processLogic?.subProcessLogicList ??
    processLogic?.subprocessLogic ??
    []
  );
}

function getSubProcessInteractionConfigurations(
  masterDetails: any,
  processLogicOptions: any[],
  processLogicId: string,
  subProcessLogicId: string
) {
  const masterSubProcessLogic = masterDetails?.subProcessLogic ?? [];
  console.log("masterSubProcessLogic", masterSubProcessLogic);
  const masterSubProcess = masterSubProcessLogic.find((logic: any) =>
    [logic.uniqueId, logic.id, logic.subProcessLogicId, logic.subLogicUniqueId].includes(subProcessLogicId)
  );

  if (masterSubProcess?.interactionConfigurations) {
    return masterSubProcess.interactionConfigurations;
  }

  const processLogic =
    processLogicOptions.find((logic: any) =>
      [logic.uniqueId, logic.id, logic.processLogicId].includes(processLogicId)
    ) ?? null;

  const subProcessLogicList = processLogic
    ? getSubProcessLogicList(processLogic)
    : processLogicOptions.flatMap(getSubProcessLogicList);

  const subProcessLogic = subProcessLogicList.find((logic: any) =>
    [logic.uniqueId, logic.id, logic.subProcessLogicId, logic.subLogicUniqueId].includes(subProcessLogicId)
  );

  return subProcessLogic?.interactionConfigurations;
}

function filterDisplayResultAnnotations(displayResult: any, ignoreDefaultLabels: string[]) {
  if (ignoreDefaultLabels.length === 0) return displayResult;

  const ignoredLabels = new Set(ignoreDefaultLabels);
  return {
    ...displayResult,
    inputDetails: displayResult.inputDetails?.map((detail: any) => ({
      ...detail,
      labelValue: {
        ...detail.labelValue,
        annotatedDetails: detail.labelValue?.annotatedDetails?.filter(
          (annotation: AnnotatedDetail) =>
            annotation.isDeleted !== 1 &&
            !ignoredLabels.has(annotation.labelUniqueId ?? '')
        ) ?? [],
      },
    })) ?? [],
  };
}

const LiveDashboard: React.FC = () => {
  const authService = Authentication();
  const [lineData, setLineData] = useState<LineEntry | null>(null);
  const [lineOptions, setLineOptions] = useState<any[]>([]);
  const [selectedLine, setSelectedLine] = useState<string>('');
  const [imageTimestamp, setImageTimestamp] = useState<string>('');
  const imgRef = useRef<HTMLImageElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [imgBounds, setImgBounds] = useState({ left: 0, top: 0, width: 0, height: 0, natW: 1, natH: 1 });
  const [imageData, setImageData] = useState<any>(null);
  const [imagePanelName, setImagePanelName] = useState<string>('');
  const [annotationColors, setAnnotationColors] = useState<string[]>([]);
  const [liveImages, setLiveImages] = useState<LiveDisplayImage[]>([]);
  const [activeLiveImageIndex, setActiveLiveImageIndex] = useState(0);
  const [relativeAgeNow, setRelativeAgeNow] = useState(Date.now());
  const selectedLineRef = useRef('');
  const cardsRef = useRef<any[]>([]);
  const lastLiveSignatureRef = useRef('');
  const hasLiveSignatureRef = useRef(false);
  const livePollIntervalRef = useRef<number | null>(null);
  const webSplitPollIntervalRef = useRef<number | null>(null);
  const pollingRequestIdRef = useRef(0);

  const getMasterDetails = () => {
    try {
      const raw = authService.decryptData("masterDetails", 1);
      return raw ?? {};
    } catch {
      return {};
    }
  };

  const getLiveDashboardMapping = (processLogicId: string): LiveDashboardMapping => {
    try {
      const siteConfigDetails = authService.decryptData("siteConfigDetails", 2);
      const reportDetails =
        typeof siteConfigDetails?.reportDetails === 'string'
          ? JSON.parse(siteConfigDetails.reportDetails)
          : siteConfigDetails?.reportDetails;
      return reportDetails?.liveDashboardMappings?.[processLogicId] ?? EMPTY_LIVE_DASHBOARD_MAPPING;
    } catch {
      return EMPTY_LIVE_DASHBOARD_MAPPING;
    }
  };

  const clearDashboardPolling = (invalidateRequests = true) => {
    if (livePollIntervalRef.current) {
      window.clearInterval(livePollIntervalRef.current);
      livePollIntervalRef.current = null;
    }
    if (webSplitPollIntervalRef.current) {
      window.clearInterval(webSplitPollIntervalRef.current);
      webSplitPollIntervalRef.current = null;
    }
    if (invalidateRequests) {
      pollingRequestIdRef.current += 1;
    }
  };

  const handleProcessLOgicSwitch = (processLogicId: any) => {
    const nextProcessLogicId = processLogicId ?? '';
    setSelectedLine(nextProcessLogicId);
    startDashboardPolling(nextProcessLogicId);
  }

  const dashboardWebSplitCall = async (processLogicId: any = '', requestId = pollingRequestIdRef.current) => {
    const { startDate, endDate } = getTodayUtcRange();
    const requestData = {
      processLogicId,
      startDate,
      endDate,
      shift: 0
    }

    const response = await dashboard.DashboardWebSplit(requestData);

    if (requestId !== pollingRequestIdRef.current || processLogicId !== selectedLineRef.current) return false;

    if (response.success) {
      const getProcessLogicId = processLogicId !== "" ? processLogicId : '';
      const cards = response.data.dashboards[0].subProcessCardsData;
      const dashboardMapping = getLiveDashboardMapping(getProcessLogicId);
      cardsRef.current = cards;
      setLineData(resolveLineData(cards, dashboardMapping));

      const previousLine = selectedLine;
      setSelectedLine(getProcessLogicId);
      selectedLineRef.current = getProcessLogicId;
      await dashboardLiveCall(getProcessLogicId, cards, previousLine, requestId, false);
      return true;
    }

    return false;
  }

  const dashboardLiveCall = async (
    processLogicId: any,
    cards: any[],
    previousLine: string,
    requestId = pollingRequestIdRef.current,
    callWebOnChange = true
  ) => {
    const { startDate, endDate } = getTodayUtcRange();
    const requestData = {
      processLogicId,
      startDate,
      endDate,
      shift: getCurrentShiftId()
    }

    const response = await dashboard.DashboardLive(requestData);

    if (requestId !== pollingRequestIdRef.current || processLogicId !== selectedLineRef.current) return;

    if (response.success) {

      const liveResults = response.data.liveResult ?? [];
      const nextLiveSignature = getLiveResultSignature(liveResults);
      const isFirstLoad = !hasLiveSignatureRef.current;
      const hasLiveResultChanged = !isFirstLoad && nextLiveSignature !== lastLiveSignatureRef.current;
      lastLiveSignatureRef.current = nextLiveSignature;
      hasLiveSignatureRef.current = true;

      if (hasLiveResultChanged && callWebOnChange) {
        dashboardWebSplitCall(processLogicId, requestId);
      }

      if (liveResults.length > 0 && (isFirstLoad || hasLiveResultChanged)) {
        const masterDetails = getMasterDetails();
        const processLogicOptions = lineOptions.length > 0 ? lineOptions : masterDetails.processLogic ?? [];
        const nextLiveImages: LiveDisplayImage[] = [];

        liveResults.forEach((liveResult: any) => {
          try {
            const parsed = JSON.parse(liveResult.displayResult);
            const interactionConfigurations = getSubProcessInteractionConfigurations(
              masterDetails,
              processLogicOptions,
              processLogicId,
              liveResult.subProcessLogicUniqueId
            );
            const ignoreDefaultLabels = parseIgnoreDefaultLabels(interactionConfigurations);
            console.log(ignoreDefaultLabels, interactionConfigurations);
            const filteredDisplayResult = filterDisplayResultAnnotations(parsed, ignoreDefaultLabels);
            const imageUrl = Object.prototype.hasOwnProperty.call(liveResult, 'sasToken')
              ? `${filteredDisplayResult.url}${liveResult.sasToken}`
              : filteredDisplayResult.url;
            const displayResult = {
              ...filteredDisplayResult,
              url: imageUrl,
            };
            const annotations = displayResult.inputDetails?.[0]?.labelValue?.annotatedDetails ?? [];

            nextLiveImages.push({
              displayResult,
              panelName: cards.find((c: any) => c.subProcessUniqueId === liveResult.subProcessLogicUniqueId)?.subProcessName ?? '',
              timestamp: formatLiveResultTime(liveResult.createdAt),
              createdAt: liveResult.createdAt,
              annotationColors: annotations.map(
                () => `hsl(${Math.floor(Math.random() * 360)}, 80%, 60%)`
              ),
            });
          } catch {
            return;
          }
        });

        if (nextLiveImages.length > 0) {
          setLiveImages(nextLiveImages);
          setActiveLiveImageIndex(0);
        }
      } else if (processLogicId !== previousLine) {
        setLiveImages([]);
        setActiveLiveImageIndex(0);
        setImageData(null);
        setImagePanelName('');
        setAnnotationColors([]);
        setImageTimestamp('');
      }

    }
  }

  const startDashboardPolling = (processLogicId: string) => {
    clearDashboardPolling();
    const requestId = pollingRequestIdRef.current;
    selectedLineRef.current = processLogicId;
    cardsRef.current = [];
    lastLiveSignatureRef.current = '';
    hasLiveSignatureRef.current = false;
    setLineData(ZERO_LINE_ENTRY);
    setLiveImages([]);
    setActiveLiveImageIndex(0);
    setImageData(null);
    setImagePanelName('');
    setAnnotationColors([]);
    setImageTimestamp('');

    dashboardWebSplitCall(processLogicId, requestId);

    livePollIntervalRef.current = window.setInterval(() => {
      dashboardLiveCall(processLogicId, cardsRef.current, processLogicId, requestId, true);
    }, LIVE_DASHBOARD_POLL_MS);

    webSplitPollIntervalRef.current = window.setInterval(() => {
      dashboardWebSplitCall(processLogicId, requestId);
    }, WEB_SPLIT_POLL_MS);
  };


  useEffect(() => {
    try {
      const raw = authService.decryptData("masterDetails", 1);
      const options = raw ? (raw).processLogic ?? [] : [];
      const initialProcessLogicId = options[0]?.uniqueId ?? '';
      setLineOptions(options);
      setSelectedLine(initialProcessLogicId);
      startDashboardPolling(initialProcessLogicId);
    } catch {
      startDashboardPolling('');
    }

    return () => clearDashboardPolling();
  }, []);

  useEffect(() => {
    if (liveImages.length === 0) return;

    const activeImage = liveImages[activeLiveImageIndex] ?? liveImages[0];
    setImageData(activeImage.displayResult);
    setImagePanelName(activeImage.panelName);
    setAnnotationColors(activeImage.annotationColors);
    setImageTimestamp(activeImage.timestamp);
  }, [activeLiveImageIndex, liveImages]);

  useEffect(() => {
    if (liveImages.length <= 1) return;

    const intervalId = window.setInterval(() => {
      setActiveLiveImageIndex((currentIndex) => (currentIndex + 1) % liveImages.length);
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [liveImages.length]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setRelativeAgeNow(Date.now());
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, []);

  const computeImgBounds = () => {
    const img = imgRef.current;
    const vp = viewportRef.current;
    if (!img || !vp || !img.naturalWidth) return;
    const vw = vp.clientWidth;
    const vh = vp.clientHeight;
    const scale = Math.min(vw / img.naturalWidth, vh / img.naturalHeight);
    const rw = img.naturalWidth * scale;
    const rh = img.naturalHeight * scale;
    setImgBounds({ left: (vw - rw) / 2, top: (vh - rh) / 2, width: rw, height: rh, natW: img.naturalWidth, natH: img.naturalHeight });
  };

  useEffect(() => {
    window.addEventListener('resize', computeImgBounds);
    return () => window.removeEventListener('resize', computeImgBounds);
  }, []);


  const line = lineData;
  if (!line) return null;

  const opStats = sideStats(line.operator);
  const nopStats = sideStats(line.nonOperator);
  const flashClass = 'alert-flash critical';
  const activeLiveImage = liveImages[activeLiveImageIndex] ?? null;
  const imageAge = activeLiveImage ? formatRelativeAge(activeLiveImage.createdAt, relativeAgeNow) : '';
  const imageAgeClass = activeLiveImage ? getRelativeAgeClass(activeLiveImage.createdAt, relativeAgeNow) : '';

  return (
    <div className="ld-root">
      <div className="ld-content-area">

        {/* Header */}
        <div className="content-header-stack">
          <div className="content-header content-header--aligned">
            <span className="breadcrumb-status-pill critical">
              <span className="status-dot" />
              <span>Defect Detected</span>
            </span>
            <div className="content-header-right">
              <div className="ld-select-wrap">
                <SelectInputWithoutForm
                  key={lineOptions[0]?.uniqueId ?? 'empty'}
                  labelName="Line"
                  id="ld-line"
                  isRequired={false}
                  options={lineOptions}
                  getOptionLabel={(opt: any) => opt.name}
                  getOptionValue={(opt: any) => opt.uniqueId}
                  defaultValue={lineOptions[0]}
                  getSelection={(opt: any) => handleProcessLOgicSwitch(opt.uniqueId)}
                  isSearchable={false}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Track body */}
        <div className="tracks-container single-track">
          <div className="track-col">
            <div className="track-body">

              {/* Data Column */}
              <div className="data-column">
                <div className="shift-summary-card">
                  <div className={flashClass} />
                  <p className="rollup-card-title">Shift Summary — {lineOptions.find((o: any) => o.uniqueId === selectedLine)?.name ?? ''}</p>
                  <div className="rollup-inner">

                    {/* Operator Side */}
                    <div className="rollup-section operator-section">
                      <div className="rollup-section-hdr operator-hdr">
                        <span className="material-icons-round">person</span>
                        <span>Operator</span>
                      </div>
                      <div className="rollup-kpi-row">
                        <div className="rollup-kpi">
                          <span className="rollup-kpi-val total">{opStats.total}</span>
                          <span className="rollup-kpi-lbl">Total Sampled</span>
                        </div>
                        <div className="rollup-kpi">
                          <span className="rollup-kpi-val critical">{opStats.defectRate.toFixed(1)}%</span>
                          <span className="rollup-kpi-lbl">Defect Rate</span>
                        </div>
                      </div>
                      <div className="rollup-sep" />
                      <div className="rollup-type-row">
                        <span className="rollup-type-lbl plate-lbl">Plate</span>
                        <div className="rollup-type-stats">
                          <div className="rollup-type-stat">
                            <span className="material-icons-round stat-icon pass-icon">check_circle</span>
                            <span className="rollup-type-pass">{line.operator.plates.total}</span>
                          </div>
                          <div className="rollup-type-stat">
                            <span className="material-icons-round stat-icon fail-icon">cancel</span>
                            <span className="rollup-type-fail">{line.operator.plates.defect}</span>
                          </div>
                        </div>
                        <span className={`rollup-type-rate ${defectBadgeClass(line.operator.plates.defect)}`}>
                          {defectRateStr(line.operator.plates.defect, line.operator.plates.total)}
                        </span>
                      </div>
                      <div className="rollup-type-row">
                        <span className="rollup-type-lbl bunch-lbl">Bunch</span>
                        <div className="rollup-type-stats">
                          <div className="rollup-type-stat">
                            <span className="material-icons-round stat-icon pass-icon">check_circle</span>
                            <span className="rollup-type-pass">{line.operator.bunches.total}</span>
                          </div>
                          <div className="rollup-type-stat">
                            <span className="material-icons-round stat-icon fail-icon">cancel</span>
                            <span className="rollup-type-fail">{line.operator.bunches.defect}</span>
                          </div>
                        </div>
                        <span className={`rollup-type-rate ${defectBadgeClass(line.operator.bunches.defect)}`}>
                          {defectRateStr(line.operator.bunches.defect, line.operator.bunches.total)}
                        </span>
                      </div>
                    </div>

                    {/* Non-Operator Side */}
                    <div className="rollup-section nop-section">
                      <div className="rollup-section-hdr nop-hdr">
                        <span className="material-icons-round">manage_accounts</span>
                        <span>Non-Operator</span>
                      </div>
                      <div className="rollup-kpi-row">
                        <div className="rollup-kpi">
                          <span className="rollup-kpi-val total">{nopStats.total}</span>
                          <span className="rollup-kpi-lbl">Total Sampled</span>
                        </div>
                        <div className="rollup-kpi">
                          <span className="rollup-kpi-val critical">{nopStats.defectRate.toFixed(1)}%</span>
                          <span className="rollup-kpi-lbl">Defect Rate</span>
                        </div>
                      </div>
                      <div className="rollup-sep" />
                      <div className="rollup-type-row">
                        <span className="rollup-type-lbl plate-lbl">Plate</span>
                        <div className="rollup-type-stats">
                          <div className="rollup-type-stat">
                            <span className="material-icons-round stat-icon pass-icon">check_circle</span>
                            <span className="rollup-type-pass">{line.nonOperator.plates.total}</span>
                          </div>
                          <div className="rollup-type-stat">
                            <span className="material-icons-round stat-icon fail-icon">cancel</span>
                            <span className="rollup-type-fail">{line.nonOperator.plates.defect}</span>
                          </div>
                        </div>
                        <span className={`rollup-type-rate ${defectBadgeClass(line.nonOperator.plates.defect)}`}>
                          {defectRateStr(line.nonOperator.plates.defect, line.nonOperator.plates.total)}
                        </span>
                      </div>
                      <div className="rollup-type-row">
                        <span className="rollup-type-lbl bunch-lbl">Bunch</span>
                        <div className="rollup-type-stats">
                          <div className="rollup-type-stat">
                            <span className="material-icons-round stat-icon pass-icon">check_circle</span>
                            <span className="rollup-type-pass">{line.nonOperator.bunches.total}</span>
                          </div>
                          <div className="rollup-type-stat">
                            <span className="material-icons-round stat-icon fail-icon">cancel</span>
                            <span className="rollup-type-fail">{line.nonOperator.bunches.defect}</span>
                          </div>
                        </div>
                        <span className={`rollup-type-rate ${defectBadgeClass(line.nonOperator.bunches.defect)}`}>
                          {defectRateStr(line.nonOperator.bunches.defect, line.nonOperator.bunches.total)}
                        </span>
                      </div>
                    </div>

                  </div>
                </div>
              </div>

              {/* Camera Feed */}
              <div className="camera-feeds">
                <div className="camera-plate-wrap">
                  <div className="cam-panel-head">
                    <div className="cam-panel-title-wrap">
                      <span className="cam-panel-title">{imagePanelName}</span>
                      {imageAge && (
                        <span className={`cam-panel-age ${imageAgeClass}`}>{imageAge}</span>
                      )}
                    </div>
                    <div className="cam-panel-meta">
                      {liveImages.length > 1 && (
                        <span className="cam-panel-count">{activeLiveImageIndex + 1} / {liveImages.length}</span>
                      )}
                      <span className="cam-panel-time">{imageTimestamp}</span>
                    </div>
                  </div>
                  <div className="feed-viewport" ref={viewportRef}>
                    {!imageData ? (
                      <div className="feed-empty">
                        <span className="material-icons-round feed-empty-icon">image_not_supported</span>
                        <span className="feed-empty-text">No defect image has been detected</span>
                      </div>
                    ) : (
                    <img
                      ref={imgRef}
                      className="feed-image"
                      src={imageData.url}
                      alt="camera feed"
                      onLoad={computeImgBounds}
                    />
                    )}
                    {imageData && (() => {
                      const annotations = (imageData.inputDetails?.[0]?.labelValue?.annotatedDetails ?? []) as AnnotatedDetail[];
                      const { natW, natH } = imgBounds;
                      return annotations.length > 0 ? (
                        <>
                          <div
                            className="annot-layer"
                            style={{ left: imgBounds.left, top: imgBounds.top, width: imgBounds.width, height: imgBounds.height, right: 'auto', bottom: 'auto' }}
                          >
                            {annotations.map((ann, i) => {
                              const [p1, p2] = ann.coordinates;
                              const x1 = Math.min(p1.x, p2.x);
                              const y1 = Math.min(p1.y, p2.y);
                              const x2 = Math.max(p1.x, p2.x);
                              const y2 = Math.max(p1.y, p2.y);
                              const left = (x1 / natW) * 100;
                              const top = (y1 / natH) * 100;
                              const width = ((x2 - x1) / natW) * 100;
                              const height = ((y2 - y1) / natH) * 100;
                              return (
                                <div
                                  key={ann.uniqueId ?? i}
                                  className="annot-box"
                                  style={{ left: `${left}%`, top: `${top}%`, right: 'auto', bottom: 'auto', width: `${width}%`, height: `${height}%`, borderColor: annotationColors[i] }}
                                />
                              );
                            })}
                          </div>
                          <div className="defect-legend">
                            {annotations.map((ann, i) => (
                              <div key={ann.uniqueId ?? i} className="defect-legend-row">
                                <div className="defect-legend-dot" style={{ background: annotationColors[i] }} />
                                <span className="defect-legend-text">{ann.labelName}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : null;
                    })()}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LiveDashboard;
